import { useMemo, useCallback } from 'react'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import { notificationService } from '@services/notificationService'
import { Notification } from '../types/notification'
import { useAuthStore } from '@store/useAuthStore'
import { notificationQueryKeys } from '@utils/notificationQueries'

const NOTIFICATIONS_PAGE_SIZE = 20
const POLL_MS = 5000

function shouldPollNotifications(): number | false {
  if (typeof document !== 'undefined' && document.hidden) {
    return false
  }
  return POLL_MS
}

export function useNotifications() {
  const { user } = useAuthStore()
  const queryClient = useQueryClient()
  const userId = user?.id

  // Buscar todas as notificações (apenas primeira página para performance)
  // Por enquanto, sempre busca página 0 para manter simples
  const {
    data: notifications = [],
    isLoading,
    refetch,
  } = useQuery<Notification[]>(
    notificationQueryKeys.list(userId!),
    () => notificationService.getAll(userId!, 0, NOTIFICATIONS_PAGE_SIZE),
    {
      enabled: !!userId,
      staleTime: 0,
      refetchOnWindowFocus: true,
      refetchInterval: shouldPollNotifications,
      keepPreviousData: true,
    }
  )

  // Buscar contagem de não lidas
  const {
    data: unreadCount = 0,
    refetch: refetchUnreadCount,
  } = useQuery<number>(
    notificationQueryKeys.unreadCount(userId!),
    () => notificationService.getUnreadCount(userId!),
    {
      enabled: !!userId,
      staleTime: 0,
      refetchOnWindowFocus: true,
      refetchInterval: shouldPollNotifications,
    }
  )

  // Marcar como lida
  const markAsReadMutation = useMutation(
    (id: number) => notificationService.markAsRead(id),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(notificationQueryKeys.list(userId!))
        queryClient.invalidateQueries(notificationQueryKeys.unreadCount(userId!))
      },
    }
  )

  // Marcar todas como lidas
  const markAllAsReadMutation = useMutation(
    () => notificationService.markAllAsRead(userId!),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(notificationQueryKeys.list(userId!))
        queryClient.invalidateQueries(notificationQueryKeys.unreadCount(userId!))
      },
    }
  )

  const markAsRead = useCallback(
    (id: number) => {
      markAsReadMutation.mutate(id)
    },
    [markAsReadMutation]
  )

  const markAllAsRead = useCallback(() => {
    markAllAsReadMutation.mutate()
  }, [markAllAsReadMutation])

  const unreadNotifications = useMemo(() => {
    return notifications.filter((n) => !n.isRead)
  }, [notifications])

  return {
    notifications,
    unreadNotifications,
    unreadCount,
    isLoading,
    markAsRead,
    markAllAsRead,
    refetch,
    refetchUnreadCount,
  }
}
